/**
 * Knowledge Article Slug Mapping
 *
 * This file provides localized slug mappings for knowledge base articles.
 * Dynamically loads locale-specific mappings to reduce bundle size.
 */

// Import all mappings statically for synchronous access
import enMapping from './slugMappings/en.json';
import deMapping from './slugMappings/de.json';
import ptBRMapping from './slugMappings/pt-BR.json';
import msMYMapping from './slugMappings/ms-MY.json';
import esMapping from './slugMappings/es.json';
import frMapping from './slugMappings/fr.json';
import jaMapping from './slugMappings/ja.json';
import idMapping from './slugMappings/id.json';

// Static mappings for synchronous access
const staticMappings: Record<string, Record<string, string>> = {
  'en': enMapping,
  'de': deMapping,
  'pt-BR': ptBRMapping,
  'ms-MY': msMYMapping,
  'es': esMapping,
  'fr': frMapping,
  'ja': jaMapping,
  'id': idMapping,
};

// Cache for loaded mappings to avoid repeated imports
const knowledgeMappingCache: Record<string, Record<string, string>> = {};

/**
 * Dynamically load knowledge slug mapping for a specific locale
 * Uses dynamic import to code-split mappings by language (75% bundle reduction)
 */
async function loadKnowledgeSlugMapping(locale: string): Promise<Record<string, string>> {
  // Return cached mapping if available
  if (knowledgeMappingCache[locale]) {
    return knowledgeMappingCache[locale];
  }

  try {
    // Dynamic import based on locale (Vite will code-split these)
    let mapping: Record<string, string>;

    switch (locale) {
      case 'de':
        mapping = (await import('./slugMappings/de.json')).default;
        break;
      case 'pt-BR':
        mapping = (await import('./slugMappings/pt-BR.json')).default;
        break;
      case 'ms-MY':
        mapping = (await import('./slugMappings/ms-MY.json')).default;
        break;
      case 'es':
        mapping = (await import('./slugMappings/es.json')).default;
        break;
      case 'fr':
        mapping = (await import('./slugMappings/fr.json')).default;
        break;
      case 'ja':
        mapping = (await import('./slugMappings/ja.json')).default;
        break;
      case 'id':
        mapping = (await import('./slugMappings/id.json')).default;
        break;
      case 'en':
      default:
        mapping = (await import('./slugMappings/en.json')).default;
        break;
    }

    // Cache the loaded mapping
    knowledgeMappingCache[locale] = mapping;
    return mapping;
  } catch (error) {
    console.error(`[KnowledgeSlugMapping] Failed to load mapping for locale "${locale}":`, error);
    // Fallback to English if loading fails
    if (locale !== 'en' && !knowledgeMappingCache['en']) {
      knowledgeMappingCache['en'] = (await import('./slugMappings/en.json')).default;
    }
    return knowledgeMappingCache['en'] || {};
  }
}

/**
 * Convert English slug to localized slug
 */
export async function getLocalizedKnowledgeSlug(englishSlug: string, locale: string): Promise<string> {
  const mapping = await loadKnowledgeSlugMapping(locale);
  return mapping[englishSlug] || englishSlug;
}

/**
 * Convert localized slug back to English slug (reverse lookup)
 */
export async function getEnglishKnowledgeSlug(localizedSlug: string, locale: string): Promise<string> {
  // If already English, return as-is
  if (locale === 'en') return localizedSlug;

  const mapping = await loadKnowledgeSlugMapping(locale);

  // Reverse lookup: find English key for this localized value
  for (const [englishSlug, localized] of Object.entries(mapping)) {
    if (localized === localizedSlug) {
      return englishSlug;
    }
  }

  // If no mapping found, return original slug
  return localizedSlug;
}

/**
 * Get full knowledge article URL with locale prefix and localized slug
 */
export async function getKnowledgeArticleUrl(englishSlug: string, locale: string): Promise<string> {
  const localizedSlug = await getLocalizedKnowledgeSlug(englishSlug, locale);

  if (locale === 'en') {
    return `/knowledge/${localizedSlug}`;
  } else if (locale === 'de') {
    return `/de/wissen/${localizedSlug}`;
  } else if (locale === 'pt-BR') {
    return `/pt-br/conhecimento/${localizedSlug}`;
  } else if (locale === 'ms-MY') {
    return `/my/pengetahuan/${localizedSlug}`;
  } else if (locale === 'es') {
    return `/es/conocimiento/${localizedSlug}`;
  } else if (locale === 'fr') {
    return `/fr/connaissances/${localizedSlug}`;
  } else if (locale === 'ja') {
    return `/ja/knowledge/${localizedSlug}`;
  } else if (locale === 'id') {
    return `/id/pengetahuan/${localizedSlug}`;
  }

  return `/knowledge/${localizedSlug}`;
}

/**
 * Get knowledge base home URL for a given locale
 */
export function getKnowledgeBaseUrl(locale: string): string {
  if (locale === 'en') return '/knowledge';
  if (locale === 'de') return '/de/wissen';
  if (locale === 'pt-BR') return '/pt-br/conhecimento';
  if (locale === 'ms-MY') return '/my/pengetahuan';
  if (locale === 'es') return '/es/conocimiento';
  if (locale === 'fr') return '/fr/connaissances';
  if (locale === 'ja') return '/ja/knowledge';
  if (locale === 'id') return '/id/pengetahuan';
  return '/knowledge';
}

/**
 * Preload knowledge slug mapping for a locale (optional optimization)
 */
export function preloadKnowledgeSlugMapping(locale: string): void {
  // Fire and forget - don't await
  loadKnowledgeSlugMapping(locale).catch(() => {
    // Silently fail - not critical
  });
}

/**
 * Get localized slug synchronously (uses static imports)
 */
export function getLocalizedKnowledgeSlugSync(englishSlug: string, locale: string): string {
  const mapping = staticMappings[locale] || staticMappings['en'];
  return mapping[englishSlug] || englishSlug;
}

/**
 * Convert localized slug back to English slug synchronously (reverse lookup)
 * Uses statically bundled mappings for SSR compatibility - no async loading needed
 */
export function getEnglishKnowledgeSlugSync(localizedSlug: string, locale: string): string {
  // If already English, return as-is
  if (locale === 'en') return localizedSlug;

  const mapping = staticMappings[locale] || staticMappings['en'];

  // Reverse lookup: find English key for this localized value
  for (const [englishSlug, localized] of Object.entries(mapping)) {
    if (localized === localizedSlug) {
      return englishSlug;
    }
  }

  // If no mapping found, return original slug (might already be English)
  return localizedSlug;
}

/**
 * Get full knowledge article URL synchronously with locale prefix and localized slug
 */
export function getKnowledgeArticleUrlSync(englishSlug: string, locale: string): string {
  const localizedSlug = getLocalizedKnowledgeSlugSync(englishSlug, locale);

  if (locale === 'en') {
    return `/knowledge/${localizedSlug}`;
  } else if (locale === 'de') {
    return `/de/wissen/${localizedSlug}`;
  } else if (locale === 'pt-BR') {
    return `/pt-br/conhecimento/${localizedSlug}`;
  } else if (locale === 'ms-MY') {
    return `/my/pengetahuan/${localizedSlug}`;
  } else if (locale === 'es') {
    return `/es/conocimiento/${localizedSlug}`;
  } else if (locale === 'fr') {
    return `/fr/connaissances/${localizedSlug}`;
  } else if (locale === 'ja') {
    return `/ja/knowledge/${localizedSlug}`;
  } else if (locale === 'id') {
    return `/id/pengetahuan/${localizedSlug}`;
  }

  return `/knowledge/${localizedSlug}`;
}
